import React, { useMemo } from 'react';
import { Calendar, RefreshCw } from 'lucide-react';
import Button from '../ui/Button';
import Dropdown from '../ui/Dropdown';
import type { AnalyticsFilters, DatePreset } from '../../types/analytics';
import { rangeFromPreset } from '../../utils/dateUtils';

interface BasketOpt { id: string; name: string; }
interface StrategyOpt { id: string; name: string; }
interface AccountOpt { broker: string; clientId: string; label: string; }

interface FilterBarProps {
  filters: AnalyticsFilters;
  onChange: (next: AnalyticsFilters) => void;
  onRefresh: () => void;
  loading?: boolean;
  baskets: BasketOpt[];
  strategies: StrategyOpt[];
  accounts: AccountOpt[];
}

const PRESETS: DatePreset[] = ['Today', '7d', '30d', '90d', 'FY', 'YTD', '1Y', 'All', 'Custom'];

const FilterBar: React.FC<FilterBarProps> = ({
  filters, onChange, onRefresh, loading, baskets, strategies, accounts,
}) => {
  // Detect active preset from current range so reload preserves selection.
  const activePreset: DatePreset = useMemo(() => {
    for (const p of PRESETS) {
      if (p === 'Custom') continue;
      const r = rangeFromPreset(p);
      if (r.start === filters.start && r.end === filters.end) return p;
    }
    return 'Custom';
  }, [filters.start, filters.end]);

  return (
    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
      <div className="flex flex-wrap items-center gap-2">
        {/* Presets */}
        <div className="flex items-center gap-1 mr-2">
          {PRESETS.filter(p => p !== 'Custom').map((p) => (
            <button
              key={p}
              onClick={() => onChange({ ...filters, ...rangeFromPreset(p) })}
              className={`px-2.5 py-1 rounded text-xs font-medium border transition ${
                activePreset === p
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
              type="button"
            >
              {p}
            </button>
          ))}
        </div>

        {/* Custom date inputs */}
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-400" />
          <input
            type="date"
            value={filters.start}
            onChange={(e) => onChange({ ...filters, start: e.target.value })}
            className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          />
          <span className="text-xs text-gray-400">to</span>
          <input
            type="date"
            value={filters.end}
            onChange={(e) => onChange({ ...filters, end: e.target.value })}
            className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          />
        </div>

        {/* Account filter */}
        {accounts.length > 0 && (
          <Dropdown
            options={[
              { value: '', label: `All accounts (${accounts.length})` },
              ...accounts.map(a => ({ value: `${a.broker}:${a.clientId}`, label: a.label })),
            ]}
            value={filters.accounts?.[0] ?? ''}
            onChange={(v) => onChange({ ...filters, accounts: v ? [v] : [] })}
          />
        )}

        {/* Basket filter */}
        {baskets.length > 0 && (
          <Dropdown
            options={[
              { value: '', label: `All baskets (${baskets.length})` },
              ...baskets.map(b => ({ value: b.id, label: b.name })),
            ]}
            value={filters.baskets?.[0] ?? ''}
            onChange={(v) => onChange({ ...filters, baskets: v ? [v] : [] })}
          />
        )}

        {/* Strategy filter */}
        {strategies.length > 0 && (
          <Dropdown
            options={[
              { value: '', label: `All strategies (${strategies.length})` },
              ...strategies.map(s => ({ value: s.id, label: s.name })),
            ]}
            value={filters.strategies?.[0] ?? ''}
            onChange={(v) => onChange({ ...filters, strategies: v ? [v] : [] })}
          />
        )}

        <div className="ml-auto">
          <Button onClick={onRefresh} variant="ghost" disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FilterBar;
